// The Northern Reach: everything east of the original map (x > mainland
// width). The meadow runs out into a band of birch and heather; past the
// snowline lie snowfields and a glacier; the coast turns to shingle and bends
// north into a fjord whose head is frozen over; and out in a colder sea there
// are islands, reached by ferry. Terrain and nature live here; the Reach's
// places are in reach_places.js.
//
// It has its own random streams (seed + 500 and up), so growing the Reach
// never moved a tree of the original map. Like the rest of the ground, every
// random sequence here is consumed in full whatever region is being painted,
// so a chunk or a tile always matches the whole.
import { rng, noise2, softShadow, wash, blobPath, samplePath, inkEllipse, inkPoly, line, withAlpha, mix, hatch, inkedCircles, clipCircles } from "./paint"
import { paintTuft, paintRock, paintLog, paintBush } from "./nature"

// Buckets of points, for "anything within r of (x, y)?" in constant time.
function pointBuckets(points, cell = 64) {
    const map = new Map()
    const key = (cx, cy) => cx * 100003 + cy
    points.forEach((p) => {
        const k = key(Math.floor(p.x / cell), Math.floor(p.y / cell))
        if (!map.has(k)) map.set(k, [])
        map.get(k).push(p)
    })
    return (x, y, r) => {
        for (let cx = Math.floor((x - r) / cell); cx <= Math.floor((x + r) / cell); cx++) {
            for (let cy = Math.floor((y - r) / cell); cy <= Math.floor((y + r) / cell); cy++) {
                const bucket = map.get(key(cx, cy))
                if (bucket) for (let i = 0; i < bucket.length; i++) if (Math.hypot(bucket[i].x - x, bucket[i].y - y) < r) return true
            }
        }
        return false
    }
}

function inPolygon(poly, x, y) {
    let inside = false
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [xi, yi] = poly[i], [xj, yj] = poly[j]
        if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
    }
    return inside
}

const tracePoly = (ctx, poly) => { ctx.beginPath(); poly.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath() }

// W and H are the Reach's own right and bottom edges (its section's bounds),
// so its random draws never depend on how big the world around it grows.
// `edges` says which of those are the world's edge (and darken).
export function buildReach(config, { W, H, Wm, edges = { right: true, bottom: true } }) {
    const seed = config.seed
    const n = noise2(seed + 500)
    const reachW = W - Wm
    const active = reachW > 0
    const northCoast = active && config.north_coast ? samplePath(config.north_coast, 8) : []

    // Where the meadow gives way to heath (a ragged line just past the old
    // edge), and where heath gives way to snow.
    const seamX = (y) => Wm + 60 + (n(7.7, y / 260) - 0.5) * 130
    const snowlineX = (y) => Wm + 950 + Math.sin(y / 640) * 170 + (n(3.1, y / 420) - 0.5) * 240 + (n(5.3, y / 110) - 0.5) * 130 + (n(8.1, y / 34) - 0.5) * 40
    const edgePoly = (fx, right) => {
        const pts = []
        for (let y = -40; y <= H + 40; y += 16) pts.push([fx(y), y])
        return [...pts, [right, H + 40], [right, -40]]
    }
    const heathPoly = active ? edgePoly(seamX, W + 40) : []
    const snowPoly = active ? edgePoly(snowlineX, W + 40) : []

    const islands = (config.islands || []).map((isl, i) => ({ ...isl, seed: seed + 520 + i }))
    const islandAt = (x, y, pad = 0) => islands.find((isl) => ((x - isl.x) / Math.max(1, isl.rx - pad)) ** 2 + ((y - isl.y) / Math.max(1, isl.ry - pad)) ** 2 < 1)
    const ice = config.ice || null
    const onIce = (x, y, pad = 0) => !!ice && ((x - ice.x) / Math.max(1, ice.rx - pad)) ** 2 + ((y - ice.y) / Math.max(1, ice.ry - pad)) ** 2 < 1
    const glacier = config.glacier || null
    const onGlacier = (x, y) => !!glacier && inPolygon(glacier, x, y)
    const lanes = (config.ferry || []).map((pts) => samplePath(pts, 10))
    const nearLane = pointBuckets(lanes.flat())
    const onFerry = (x, y, pad = 26) => lanes.length > 0 && nearLane(x, y, pad)
    const nearNorthCoast = pointBuckets(northCoast)

    function biome(x, y) {
        const isl = islandAt(x, y, -20)
        if (isl) return isl.biome
        if (x < Wm || x < seamX(y)) return "meadow"
        return x < snowlineX(y) ? "birch" : "snow"
    }

    // The ground palette for a spot: path and clearing colours become packed
    // snow on the snowfields, so clearings and trails there read as trodden.
    function groundPal(pal, x, y) {
        if (biome(x, y) !== "snow" && !onIce(x, y) && !onGlacier(x, y)) return pal
        return trailPal(pal)
    }
    const trailPals = new Map()
    function trailPal(pal) {
        if (!trailPals.has(pal)) trailPals.set(pal, { ...pal, path: pal.snowTrail, pathLight: pal.snowLight, pathDark: pal.snowTrailDark, pebble: pal.stoneLight })
        return trailPals.get(pal)
    }

    // --- the land -----------------------------------------------------------------

    function paintLand(ctx, pal, vis) {
        if (!active) return
        const area = (reachW * H) / (2000 * 1250)
        // heath: dry grass, heather and moss, from the seam east
        ctx.save()
        tracePoly(ctx, heathPoly)
        ctx.fillStyle = pal.heath; ctx.fill()
        ctx.clip()
        const r = rng(seed + 501)
        for (let i = 0; i < 60 * area; i++) {
            const x = Wm + r() * reachW, y = r() * H, rad = 70 + r() * 200, a = 0.3 + r() * 0.3
            if (!vis(x, y, rad)) continue
            const v = n(x / 280, y / 280)
            const color = v > 0.66 ? pal.heather : v > 0.5 ? pal.heathLight : v > 0.36 ? pal.heathDark : pal.groundDark
            wash(ctx, x, y, rad, color, v > 0.66 ? a * 0.3 : a)
        }
        for (let i = 0; i < 5200 * area; i++) {
            const x = Wm + r() * reachW, y = r() * H
            if (!vis(x, y, 4)) continue
            const v = n(x / 36, y / 36)
            ctx.fillStyle = v > 0.62 ? withAlpha(pal.heathLight, 0.55) : v < 0.33 ? withAlpha(pal.heathDark, 0.5) : withAlpha(pal.heather, 0.18)
            ctx.beginPath(); ctx.ellipse(x, y, 2.4, 1.3, 0, 0, Math.PI * 2); ctx.fill()
        }
        ctx.restore()
        // the meadow bleeds a little way into the heath, so the seam is soft
        const rs = rng(seed + 502)
        for (let y = -20; y < H + 40; y += 55) {
            const x = seamX(y), rad = 60 + rs() * 70
            if (!vis(x, y, rad + 40)) continue
            wash(ctx, x - 10, y, rad, pal.ground, 0.7)
            wash(ctx, x + 40, y + 20, rad * 0.8, pal.heathLight, 0.35)
        }

        const edge = 160
        // the heath darkens toward the world's top and bottom edges (the snow,
        // painted next, covers its eastern part)
        ctx.save()
        tracePoly(ctx, heathPoly); ctx.clip()
        ;[[0, 0, 0, edge], [0, H, 0, H - edge]].forEach(([x0, y0, x1, y1]) => {
            const g = ctx.createLinearGradient(x0, y0, x1, y1)
            g.addColorStop(0, withAlpha(pal.heathDark, 0.75)); g.addColorStop(1, withAlpha(pal.heathDark, 0))
            ctx.fillStyle = g; ctx.fillRect(Wm, y0 === 0 ? 0 : H - edge, reachW, edge)
        })
        ctx.restore()
        // snow patches thicken toward the snowline, then the snowfield itself
        const rp = rng(seed + 503)
        for (let i = 0; i < 360 * (H / 5000); i++) {
            const y = rp() * H, depth = rp() * rp() * 520, rx = 30 + rp() * 70, ry = 14 + rp() * 26, sd = Math.floor(rp() * 1e6)
            const x = snowlineX(y) - depth
            if (!vis(x, y, rx + 10)) continue
            blobPath(ctx, x, y, rx, ry, sd, 0.2, 18)
            ctx.fillStyle = withAlpha(pal.snowShade, 0.9); ctx.fill()
            blobPath(ctx, x - 2, y - 2, rx * 0.9, ry * 0.85, sd + 1, 0.2, 18)
            ctx.fillStyle = pal.snow; ctx.fill()
        }
        ctx.save()
        tracePoly(ctx, snowPoly)
        // a drifted lip along the snowline, then the snow itself
        ctx.strokeStyle = withAlpha(pal.snowDeep, 0.8); ctx.lineWidth = 7; ctx.lineJoin = "round"; ctx.stroke()
        ctx.fillStyle = pal.snow; ctx.fill()
        ctx.clip()
        const rw = rng(seed + 504)
        // blue shadow pools and bright rises, following the noise
        for (let i = 0; i < 70 * area; i++) {
            const x = Wm + 400 + rw() * (reachW - 400), y = rw() * H, rad = 90 + rw() * 260, a = 0.35 + rw() * 0.3
            if (!vis(x, y, rad)) continue
            const v = n(x / 330, y / 330)
            wash(ctx, x, y, rad, v > 0.58 ? pal.snowLight : v > 0.42 ? pal.snowShade : pal.snowDeep, v > 0.58 ? a * 0.8 : a * 0.6)
        }
        // wind-drift ridges: a bright crest with a blue shadow under it
        for (let i = 0; i < 190 * area; i++) {
            const x = Wm + 400 + rw() * (reachW - 400), y = rw() * H, len = 40 + rw() * 110, bow = (rw() - 0.3) * 18, tilt = -0.25 + (rw() - 0.5) * 0.3
            if (!vis(x, y, len)) continue
            const dx = Math.cos(tilt) * len, dy = Math.sin(tilt) * len
            ctx.lineCap = "round"
            ctx.strokeStyle = withAlpha(pal.snowDeep, 0.55); ctx.lineWidth = 2.2
            ctx.beginPath(); ctx.moveTo(x - dx / 2, y - dy / 2 + 2.5); ctx.quadraticCurveTo(x, y + bow + 2.5, x + dx / 2, y + dy / 2 + 2.5); ctx.stroke()
            ctx.strokeStyle = withAlpha(pal.snowLight, 0.9); ctx.lineWidth = 1.6
            ctx.beginPath(); ctx.moveTo(x - dx / 2, y - dy / 2); ctx.quadraticCurveTo(x, y + bow, x + dx / 2, y + dy / 2); ctx.stroke()
        }
        // sparkle and grain
        for (let i = 0; i < 3600 * area; i++) {
            const x = Wm + 400 + rw() * (reachW - 400), y = rw() * H, big = rw() < 0.08
            if (!vis(x, y, 3)) continue
            ctx.fillStyle = big ? pal.snowLight : withAlpha(n(x / 30, y / 30) > 0.5 ? pal.snowShade : pal.snowLight, 0.6)
            ctx.beginPath(); ctx.arc(x, y, big ? 1.3 : 0.8, 0, Math.PI * 2); ctx.fill()
        }
        // the woods darken toward the edges of the world, blue on snow
        ;[edges.right && [W, 0, W - edge, 0, W - edge, 0, edge, H], [0, 0, 0, edge, Wm, 0, reachW, edge], edges.bottom && [0, H, 0, H - edge, Wm, H - edge, reachW, edge]].filter(Boolean).forEach(([x0, y0, x1, y1, rx, ry, rwid, rh]) => {
            const g = ctx.createLinearGradient(x0, y0, x1, y1)
            g.addColorStop(0, withAlpha(pal.snowDeep, 0.8)); g.addColorStop(1, withAlpha(pal.snowDeep, 0))
            ctx.fillStyle = g; ctx.fillRect(rx, ry, rwid, rh)
        })
        ctx.restore()
    }

    // The glacier: a tongue of snowy ice from the top of the map into the
    // fjord's head. Blue in its folds and along its flow, torn by fields of
    // crevasses where it steepens, with moraines of grey grit along its sides.
    function smoothPoly(ctx, poly) {
        ctx.beginPath()
        const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
        const start = mid(poly[poly.length - 1], poly[0])
        ctx.moveTo(start[0], start[1])
        poly.forEach((p, i) => { const m = mid(p, poly[(i + 1) % poly.length]); ctx.quadraticCurveTo(p[0], p[1], m[0], m[1]) })
        ctx.closePath()
    }
    function paintGlacier(ctx, pal, vis) {
        if (!glacier) return
        const xs = glacier.map(([x]) => x), ys = glacier.map(([, y]) => y)
        const bx = Math.min(...xs), by = Math.min(...ys), bw = Math.max(...xs) - bx, bh = Math.max(...ys) - by
        const r = rng(seed + 505)
        const fields = Array.from({ length: 5 }, () => ({ t: 0.12 + r() * 0.78, n: 18 + Math.floor(r() * 14), s: r() }))
        const slits = fields.map((f) => Array.from({ length: f.n }, () => [r(), r(), r(), r()]))
        if (!vis(bx + bw / 2, by + bh / 2, Math.hypot(bw, bh) / 2 + 40)) return
        // the edges of the ice at height y (the polygon's left and right)
        const edgesAt = (y) => {
            const hits = []
            for (let i = 0, j = glacier.length - 1; i < glacier.length; j = i++) {
                const [x1, y1] = glacier[i], [x2, y2] = glacier[j]
                if ((y1 > y) !== (y2 > y)) hits.push(x1 + ((y - y1) / (y2 - y1)) * (x2 - x1))
            }
            return hits.length >= 2 ? [Math.min(...hits), Math.max(...hits)] : null
        }
        ctx.save()
        smoothPoly(ctx, glacier)
        ctx.strokeStyle = withAlpha(pal.snowDeep, 0.9); ctx.lineWidth = 10; ctx.stroke()
        const g = ctx.createLinearGradient(bx, 0, bx + bw, 0)
        g.addColorStop(0, mix(pal.glacier, pal.snowLight, 0.55)); g.addColorStop(0.6, mix(pal.glacier, pal.snowLight, 0.3)); g.addColorStop(1, pal.glacier)
        ctx.fillStyle = g; ctx.fill()
        ctx.clip()
        // blue bands along the flow
        for (let y = by; y < by + bh; y += 60) {
            const e = edgesAt(y)
            if (!e || !vis((e[0] + e[1]) / 2, y, (e[1] - e[0]) / 2 + 60)) continue
            ;[0.3, 0.72].forEach((t, k) => wash(ctx, e[0] + (e[1] - e[0]) * t, y, 70, k ? pal.glacierDeep : pal.glacier, 0.35))
        }
        // crevasse fields: short dark slits across the flow
        fields.forEach((f, fi) => {
            const y0 = by + bh * f.t, e = edgesAt(y0)
            if (!e) return
            slits[fi].forEach(([a, b, c, d]) => {
                const y = y0 + (a - 0.5) * 120, ee = edgesAt(y) || e
                const x = ee[0] + 24 + b * Math.max(10, ee[1] - ee[0] - 48), len = 12 + c * 22
                if (!vis(x, y, len)) return
                ctx.strokeStyle = withAlpha(pal.glacierDeep, 0.95); ctx.lineWidth = 2.2 + d; ctx.lineCap = "round"
                ctx.beginPath(); ctx.moveTo(x - len / 2, y + d * 3); ctx.quadraticCurveTo(x, y - 2, x + len / 2, y + (1 - d) * 3); ctx.stroke()
                ctx.strokeStyle = withAlpha(pal.snowLight, 0.9); ctx.lineWidth = 1
                ctx.beginPath(); ctx.moveTo(x - len / 2, y + d * 3 - 2.5); ctx.quadraticCurveTo(x, y - 4.5, x + len / 2, y + (1 - d) * 3 - 2.5); ctx.stroke()
            })
        })
        // moraines: grit down both sides and one down the middle
        for (let y = by; y < by + bh; y += 9) {
            const e = edgesAt(y)
            if (!e) continue
            ;[16, (e[1] - e[0]) / 2, e[1] - e[0] - 16].forEach((o, k) => {
                const x = e[0] + o + (n(k * 7 + 1, y / 70) - 0.5) * (k === 1 ? 30 : 8)
                if (!vis(x, y, 6)) return
                ctx.fillStyle = withAlpha(pal.stoneDark, k === 1 ? 0.45 : 0.6)
                ctx.beginPath(); ctx.arc(x + (n(y, k) - 0.5) * 6, y, k === 1 ? 1.2 : 1.7, 0, Math.PI * 2); ctx.fill()
            })
        }
        ctx.restore()
        // the ice cliff where it meets the fjord
        const foot = edgesAt(by + bh - 30)
        if (foot && vis((foot[0] + foot[1]) / 2, by + bh, 200)) {
            for (let x = foot[0] + 6; x < foot[1] - 6; x += 7) line(ctx, [[x, by + bh - 40], [x + 1, by + bh - 14]], withAlpha(pal.glacierDeep, 0.8), 1.6)
        }
    }

    // --- the shore and the sea ------------------------------------------------------

    // Grey shingle along the Reach's coast, painted before the sea so the
    // water covers its seaward half.
    // Inside the fjord the shore is rock, not a beach: a narrow grey bank.
    const inFjord = (p) => p.y < 2560 && p.x > 5560
    const shoreRuns = []
    northCoast.forEach((p, i) => {
        const kind = inFjord(p) ? "rock" : "shingle"
        const run = shoreRuns[shoreRuns.length - 1]
        if (!run || run.kind !== kind) shoreRuns.push({ kind, pts: i ? [northCoast[i - 1], p] : [p] })
        else run.pts.push(p)
    })
    function paintShore(ctx, pal, vis) {
        if (!northCoast.length || !northCoast.some((p) => vis(p.x, p.y, 200))) return
        ctx.lineCap = "round"; ctx.lineJoin = "round"
        const stroke = (pts, width, color) => { ctx.strokeStyle = color; ctx.lineWidth = width; ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke() }
        shoreRuns.forEach((run) => {
            if (run.kind === "shingle") { stroke(run.pts, 210, pal.shingle); stroke(run.pts, 80, pal.shingleDark) } else { stroke(run.pts, 70, pal.stoneDark); stroke(run.pts, 36, pal.stone) }
        })
    }

    // After the sea: colder water toward the east, and deeper out in the
    // northern sea.
    function paintSeaTint(ctx, pal, seaPolygon, vis) {
        if (!active || !seaPolygon) return
        if (!vis(Wm + reachW / 2, H * 0.7, Math.max(reachW, H))) return
        ctx.save()
        ctx.beginPath(); seaPolygon.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
        const g = ctx.createLinearGradient(Wm - 200, 0, Wm + 1400, 0)
        g.addColorStop(0, withAlpha(pal.seaCold, 0)); g.addColorStop(1, withAlpha(pal.seaCold, 0.55))
        // the tint carries on past the Reach, across all the sea east of it
        const tintW = Math.max(reachW, (config.width || W) - Wm) + 260
        ctx.fillStyle = g; ctx.fillRect(Wm - 200, 0, tintW, H + 60)
        // and fades out southward across the Middle Sea, in thin bands
        if ((config.height || H) > H + 60) {
            for (let k = 0; k < 40; k++) {
                ctx.globalAlpha = 1 - (k + 1) / 41
                ctx.fillRect(Wm - 200, H + 60 + k * 30, tintW, 30.5)
            }
            ctx.globalAlpha = 1
        }
        const cx = Wm + reachW * 0.7, cy = H + 500
        const d = ctx.createRadialGradient(cx, cy, 200, cx, cy, 2900)
        d.addColorStop(0, withAlpha(pal.seaColdDeep, 0.55)); d.addColorStop(1, withAlpha(pal.seaColdDeep, 0))
        ctx.fillStyle = d; ctx.fillRect(Wm - 200, 1600, tintW, Math.max(H - 1540, (config.height || H) - 1600))
        ctx.restore()
    }

    // Pebbles, wrack and driftwood on the shingle's land side.
    function paintShoreTexture(ctx, pal, vis) {
        const r = rng(seed + 506)
        northCoast.forEach((p, i) => {
            const rocky = inFjord(p)
            for (let k = 0; k < 5; k++) {
                const o = 8 + r() * (rocky ? 30 : 96), jitter = (r() - 0.5) * 16, size = 1 + r() * 2.2, kind = r()
                if (!vis(p.x, p.y, 110)) continue
                const x = p.x - p.nx * o + p.ny * jitter, y = p.y - p.ny * o - p.nx * jitter
                if (kind < 0.12) inkEllipse(ctx, x, y, size + 2, size + 0.8, pal.stoneLight, withAlpha(pal.ink, 0.45), 0.7)
                else { ctx.fillStyle = kind < 0.5 ? withAlpha(pal.shingleDark, 0.8) : withAlpha(pal.stoneLight, 0.7); ctx.beginPath(); ctx.arc(x, y, size, 0, Math.PI * 2); ctx.fill() }
            }
            // a line of wrack at the tideline
            if (i % 3 === 0 && vis(p.x, p.y, 60)) {
                ctx.fillStyle = withAlpha(pal.night ? "#2C3A2A" : "#6B7A45", 0.7)
                ctx.beginPath(); ctx.ellipse(p.x - p.nx * 6, p.y - p.ny * 6, 5 + (i % 5), 1.6, Math.atan2(p.ny, p.nx) + Math.PI / 2, 0, Math.PI * 2); ctx.fill()
            }
        })
    }

    // Islands: shallows and surf round a beach, then heath or snow.
    function paintIslands(ctx, pal, vis) {
        islands.forEach((isl) => {
            const r = rng(isl.seed)
            const draws = Array.from({ length: 900 }, () => [r(), r(), r(), r()])
            if (!vis(isl.x, isl.y, Math.max(isl.rx, isl.ry) + 140)) return
            const beach = isl.biome === "snow" ? pal.shingle : pal.sand
            blobPath(ctx, isl.x, isl.y, isl.rx + 90, isl.ry + 70, isl.seed, 0.07, 36)
            ctx.fillStyle = withAlpha(pal.seaShallow, 0.75); ctx.fill()
            blobPath(ctx, isl.x, isl.y, isl.rx + 34, isl.ry + 26, isl.seed + 1, 0.07, 36)
            ctx.fillStyle = withAlpha(pal.foam, 0.5); ctx.fill()
            blobPath(ctx, isl.x, isl.y, isl.rx + 18, isl.ry + 14, isl.seed + 1, 0.07, 36)
            ctx.fillStyle = beach; ctx.fill()
            ctx.strokeStyle = pal.foam; ctx.lineWidth = 3; ctx.globalAlpha = 0.85; ctx.stroke(); ctx.globalAlpha = 1
            ctx.save()
            blobPath(ctx, isl.x, isl.y, isl.rx - 30, isl.ry - 24, isl.seed + 2, 0.09, 36)
            ctx.fillStyle = isl.biome === "snow" ? pal.snow : pal.heath; ctx.fill()
            ctx.clip()
            draws.forEach(([a, b, c, d], i) => {
                const x = isl.x + (a - 0.5) * isl.rx * 2, y = isl.y + (b - 0.5) * isl.ry * 2
                if (i < 40) {
                    const color = isl.biome === "snow" ? (c > 0.5 ? pal.snowShade : pal.snowLight) : (c > 0.66 ? pal.heather : c > 0.33 ? pal.heathLight : pal.heathDark)
                    wash(ctx, x, y, 50 + d * 120, color, 0.4)
                } else {
                    ctx.fillStyle = isl.biome === "snow" ? withAlpha(c > 0.5 ? pal.snowShade : pal.snowLight, 0.7) : withAlpha(c > 0.5 ? pal.heathLight : pal.heathDark, 0.55)
                    ctx.beginPath(); ctx.ellipse(x, y, 2.4, 1.3, 0, 0, Math.PI * 2); ctx.fill()
                }
            })
            ctx.restore()
            // a soft edge where the land meets the beach
            ctx.save()
            blobPath(ctx, isl.x, isl.y, isl.rx - 30, isl.ry - 24, isl.seed + 2, 0.09, 36)
            ctx.strokeStyle = withAlpha(isl.biome === "snow" ? pal.snowShade : pal.heathDark, 0.7); ctx.lineWidth = 6; ctx.stroke()
            ctx.restore()
            // pebbles on the beach
            const rb = rng(isl.seed + 3)
            for (let i = 0; i < 160; i++) {
                const a = rb() * Math.PI * 2, d = 0.9 + rb() * 0.14, s = 1 + rb() * 2
                const x = isl.x + Math.cos(a) * isl.rx * d, y = isl.y + Math.sin(a) * isl.ry * d
                ctx.fillStyle = withAlpha(isl.biome === "snow" ? pal.shingleDark : pal.sandDark, 0.8)
                ctx.beginPath(); ctx.arc(x, y, s, 0, Math.PI * 2); ctx.fill()
            }
        })
        ;(config.skerries || []).forEach(([x, y, s], i) => {
            if (!vis(x, y, s + 40)) return
            blobPath(ctx, x, y, s + 22, s * 0.6 + 16, seed + 540 + i, 0.2, 16)
            ctx.fillStyle = withAlpha(pal.foam, 0.45); ctx.fill()
            ctx.save(); ctx.translate(x, y + s * 0.2); paintRock(ctx, pal, s, seed + 550 + i); ctx.restore()
        })
    }

    // The frozen head of the fjord: ice with cracks and drifted snow.
    function paintIce(ctx, pal, vis) {
        if (!ice || !vis(ice.x, ice.y, ice.rx + 40)) return
        ctx.save()
        blobPath(ctx, ice.x, ice.y, ice.rx + 10, ice.ry + 8, seed + 560, 0.08, 30)
        ctx.fillStyle = withAlpha(pal.iceDeep, 0.9); ctx.fill()
        blobPath(ctx, ice.x, ice.y, ice.rx, ice.ry, seed + 560, 0.08, 30)
        const g = ctx.createRadialGradient(ice.x - ice.rx * 0.3, ice.y - ice.ry * 0.3, 10, ice.x, ice.y, ice.rx)
        g.addColorStop(0, mix(pal.ice, pal.snowLight, 0.4)); g.addColorStop(1, pal.ice)
        ctx.fillStyle = g; ctx.fill()
        ctx.strokeStyle = withAlpha(pal.ink, 0.6); ctx.lineWidth = 1.2; ctx.stroke()
        ctx.clip()
        const r = rng(seed + 561)
        for (let i = 0; i < 16; i++) {
            let x = ice.x + (r() - 0.5) * ice.rx * 1.6, y = ice.y + (r() - 0.5) * ice.ry * 1.6
            ctx.strokeStyle = withAlpha(pal.iceCrack, 0.8); ctx.lineWidth = 1
            ctx.beginPath(); ctx.moveTo(x, y)
            for (let k = 0; k < 4; k++) { x += (r() - 0.5) * 60; y += (r() - 0.5) * 40; ctx.lineTo(x, y) }
            ctx.stroke()
        }
        for (let i = 0; i < 10; i++) wash(ctx, ice.x + (r() - 0.5) * ice.rx * 1.5, ice.y + (r() - 0.5) * ice.ry * 1.4, 30 + r() * 50, pal.snowLight, 0.6)
        ctx.restore()
    }

    // Ferry lanes: a faint wake, and wooden jetties where each one leaves the
    // shore. (The buoys bob in the motion layer.)
    function paintFerry(ctx, pal, vis) {
        lanes.forEach((lane) => {
            if (!lane.some((p) => vis(p.x, p.y, 40))) return
            ctx.save()
            ctx.strokeStyle = withAlpha(pal.foam, 0.28); ctx.lineWidth = 16; ctx.lineCap = "round"; ctx.lineJoin = "round"
            ctx.setLineDash([2, 26])
            ctx.beginPath(); lane.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke()
            ctx.restore()
            ;[lane, [...lane].reverse()].forEach((ends) => jetty(ctx, pal, ends))
        })
    }
    function jetty(ctx, pal, pts) {
        const a = pts[0], b = pts[Math.min(pts.length - 1, 12)]
        const angle = Math.atan2(b.y - a.y, b.x - a.x), len = Math.min(130, Math.hypot(b.x - a.x, b.y - a.y)), half = 15
        ctx.save()
        ctx.translate(a.x, a.y); ctx.rotate(angle)
        softShadow(ctx, len / 2 + 6, half + 8, len / 2 + 4, 9, 0.28, pal.shadow)
        ctx.fillStyle = pal.woodLight; ctx.fillRect(0, -half, len, half * 2)
        ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.5; ctx.strokeRect(0, -half, len, half * 2)
        for (let x = 7; x < len; x += 8) line(ctx, [[x, -half], [x, half]], pal.woodDark, 1, 0.7)
        ;[[len - 4, -half], [len - 4, half], [len * 0.5, -half], [len * 0.5, half]].forEach(([x, y]) => inkEllipse(ctx, x, y, 3.6, 3.6, pal.woodDark, pal.ink, 1.1))
        ctx.restore()
    }

    // --- nature ------------------------------------------------------------------------
    // Spruce, birch, boulders, juniper and driftwood, grown once from the
    // Reach's own stream. `clearOf` and `spaced` come from the world, so
    // nothing grows on a road, a place, or another tree.
    function grow({ clearOf, spaced, note, inSea, keep = () => true }) {
        const sprites = [], footprints = [], trees = [], smalls = []
        if (!active) return { sprites, footprints, trees, smalls }
        const r = rng(seed + 530)
        const grove = noise2(seed + 531)
        const target = Math.round(170 * (reachW * H * 0.45) / (2000 * 1250) * 0.62)
        for (let i = 0; i < target * 16 && trees.length < target; i++) {
            const onIsland = i % 7 === 0 && islands.length
            const isl = onIsland ? islands[Math.floor(r() * islands.length)] : null
            const a = r() * Math.PI * 2, d = Math.sqrt(r())
            const x = isl ? isl.x + Math.cos(a) * (isl.rx - 60) * d : Wm + 40 + r() * (reachW - 80)
            const y = isl ? isl.y + Math.sin(a) * (isl.ry - 50) * d : 40 + r() * (H - 80)
            const g = grove(x / 380, y / 380), roll = r(), size0 = r()
            const b = biome(x, y)
            if (b === "meadow" || onIce(x, y, -30) || onGlacier(x, y) || onFerry(x, y, 60)) continue
            if (inSea(x, y, 70)) continue
            if (b === "birch" ? g < 0.44 : g < (isl ? 0.4 : 0.6)) continue
            const kind = b === "birch" ? (roll < 0.62 ? "birch" : "spruce") : "spruce"
            const s = kind === "spruce" ? 18 + size0 * 13 : 15 + size0 * 9
            if (!clearOf(x, y, s * 0.6) || !spaced(x, y, s)) continue
            const t = { x, y, s, kind, snowy: b === "snow", seed: Math.floor(r() * 1e6) }
            trees.push(t); note(t)
        }
        for (let i = trees.length - 1; i >= 0; i--) if (!keep(trees[i])) trees.splice(i, 1)
        trees.forEach((t) => {
            if (t.kind === "birch") {
                sprites.push({ x: t.x, y: t.y, box: [-t.s * 1.3, -t.s * 3.4, t.s * 1.3, 6], kind: "tree", sway: 1.1, seed: t.seed, paint(ctx, pal) { paintBirch(ctx, pal, t.s, t.seed) } })
            } else {
                sprites.push({ x: t.x, y: t.y, box: [-t.s * 1.05, -t.s * 3.9, t.s * 1.05, 6], kind: "tree", sway: 0.55, seed: t.seed, paint(ctx, pal) { paintSpruce(ctx, pal, t.s, t.seed, t.snowy) } })
            }
            footprints.push({ circle: true, x: t.x, y: t.y - 2, r: t.s * 0.26 + 4 })
        })
        // boulders, juniper and driftwood
        const place = (count, kind, sizeFn, test) => {
            for (let i = 0, made = 0; i < count * 30 && made < count; i++) {
                const x = Wm + 40 + r() * (reachW - 80), y = 40 + r() * (H - 80), s = sizeFn(), sd = Math.floor(r() * 1e6)
                if (!test(x, y) || !clearOf(x, y, 8) || !spaced(x, y, s)) continue
                const o = { x, y, s, kind, snowy: biome(x, y) === "snow", seed: sd }
                smalls.push(o); note(o); made++
            }
        }
        const reachArea = (reachW * H) / (2000 * 1250)
        place(Math.round(14 * reachArea), "boulder", () => 10 + r() * 14, (x, y) => biome(x, y) !== "meadow" && !inSea(x, y, 60) && !onGlacier(x, y))
        place(Math.round(16 * reachArea), "juniper", () => 10 + r() * 6, (x, y) => biome(x, y) === "birch" && !inSea(x, y, 70))
        place(Math.round(5 * reachArea), "driftwood", () => 20 + r() * 8, (x, y) => nearNorthCoast(x, y, 150) && !nearNorthCoast(x, y, 40) && !inSea(x, y, 0))
        for (let i = smalls.length - 1; i >= 0; i--) if (!keep(smalls[i])) smalls.splice(i, 1)
        smalls.forEach((o) => {
            if (o.kind === "boulder") {
                sprites.push({ x: o.x, y: o.y, box: [-o.s * 1.4, -o.s * 1.3, o.s * 1.4, o.s * 0.8 + 4], kind: "rock", sway: 0, paint(ctx, pal) { paintBoulder(ctx, pal, o.s, o.seed, o.snowy) } })
                footprints.push({ circle: true, x: o.x, y: o.y - 2, r: o.s * 0.8 })
            } else if (o.kind === "juniper") {
                sprites.push({ x: o.x, y: o.y, box: [-o.s * 1.7, -o.s * 2.2, o.s * 1.7, o.s * 0.8 + 4], kind: "bush", sway: 0.3, paint(ctx, pal) { paintBush(ctx, juniperPal(pal), o.s, o.seed) } })
            } else {
                sprites.push({ x: o.x, y: o.y, box: [-o.s * 1.4, -o.s * 0.8, o.s * 1.4, o.s * 0.8], kind: "log", sway: 0, paint(ctx, pal) { paintLog(ctx, driftPal(pal), o.s, ((o.seed % 9) / 9 - 0.5) * 1.6) } })
                footprints.push({ circle: true, x: o.x, y: o.y - 2, r: o.s * 0.6 })
            }
        })
        return { sprites, footprints, trees, smalls }
    }

    // Shadows under the Reach's trees and rocks: blue on snow.
    function paintShadows(ctx, pal, vis, grown) {
        const blue = pal.night ? "0,8,20" : "60,90,130"
        grown.trees.forEach((t) => { if (vis(t.x, t.y, t.s * 2)) softShadow(ctx, t.x + t.s * 0.45, t.y + 2, t.s * 1.15, t.s * 0.45, pal.shadowAlpha * (t.snowy ? 1.1 : 1), t.snowy ? blue : pal.shadow) })
        grown.smalls.forEach((o) => { if (vis(o.x, o.y, o.s * 2)) softShadow(ctx, o.x + o.s * 0.3, o.y + 1, o.s * 1.2, o.s * 0.45, pal.shadowAlpha * 0.8, o.snowy ? blue : pal.shadow) })
    }

    // Flat details over everything else: heather and lingonberries on the
    // heath, cottongrass, dry tufts, and a fox's tracks across the snow.
    function paintDetails(ctx, pal, vis, clearOf, inSea) {
        if (!active) return
        const area = (reachW * H) / (2000 * 1250)
        const r = rng(seed + 507)
        for (let i = 0; i < 70 * area; i++) {
            const x = Wm + r() * reachW, y = r() * H, kind = r(), sd = Math.floor(r() * 1e6)
            if (!vis(x, y, 20)) continue
            const b = biome(x, y)
            if (b === "meadow" || inSea(x, y, 40) || onGlacier(x, y) || onIce(x, y) || !clearOf(x, y, -10)) continue
            if (b === "birch") (kind < 0.7 ? heatherClump : lingon)(ctx, pal, x, y, sd)
            else if (kind < 0.35) cottongrass(ctx, pal, x, y, sd)
        }
        ctx.lineWidth = 1.1
        for (let i = 0; i < 1500 * area; i++) {
            const x = Wm + r() * reachW, y = r() * H, h = 4 + r() * 4
            if (!vis(x, y, 8)) continue
            const b = biome(x, y)
            if (b === "meadow" || inSea(x, y, 60) || onGlacier(x, y) || onIce(x, y) || !clearOf(x, y, -14)) continue
            if (b === "snow" && n(x / 70, y / 70) < 0.62) continue // only a few tufts poke through the snow
            ctx.globalAlpha = 0.35 + n(x / 90, y / 90) * 0.4
            paintTuft(ctx, b === "snow" ? pal.heathDark : (n(x / 50, y / 50) > 0.5 ? pal.heathLight : pal.heathDark), x, y, h)
        }
        ctx.globalAlpha = 1
        // fox tracks: a wandering line of paired prints
        for (let t = 0; t < 7; t++) {
            let x = Wm + 1200 + r() * (reachW - 1400), y = 200 + r() * 2200, dir = r() * Math.PI * 2
            for (let s = 0; s < 70; s++) {
                dir += (r() - 0.5) * 0.35
                x += Math.cos(dir) * 14; y += Math.sin(dir) * 14
                if (!vis(x, y, 6) || biome(x, y) !== "snow") continue
                const side = s % 2 ? 1 : -1
                ctx.fillStyle = withAlpha(pal.snowDeep, 0.8)
                ctx.beginPath(); ctx.ellipse(x - Math.sin(dir) * 3 * side, y + Math.cos(dir) * 3 * side, 1.7, 2.3, dir, 0, Math.PI * 2); ctx.fill()
            }
        }
    }

    // How far (x, y) is from the snowfields: 0 on the snow, else how far west
    // of the snowline (for the wind you hear out there).
    function snowDistance(x, y) {
        if (!active || x < Wm) return Infinity
        const b = biome(x, y)
        if (b === "snow") return 0
        return b === "birch" ? Math.max(0, snowlineX(y) - x) : Infinity
    }

    return {
        active, northCoast, snowDistance, islands, lanes, ice, glacier, biome, groundPal, trailPal, islandAt, onIce, onGlacier, onFerry, snowPoly,
        paintLand, paintGlacier, paintShore, paintSeaTint, paintShoreTexture, paintIslands, paintIce, paintFerry, grow, paintShadows, paintDetails
    }
}

// ---- painters -------------------------------------------------------------------------

const juniperCache = new Map()
function juniperPal(pal) {
    if (!juniperCache.has(pal)) juniperCache.set(pal, { ...pal, leaf: pal.spruceLight, leafDark: pal.spruceDark, leafLight: mix(pal.spruceLight, pal.snowLight, 0.25), berry: pal.night ? "#3E4A6A" : "#5A6F9E", flowers: [pal.night ? "#3E4A6A" : "#5A6F9E"] })
    return juniperCache.get(pal)
}
const driftCache = new Map()
function driftPal(pal) {
    if (!driftCache.has(pal)) driftCache.set(pal, { ...pal, woodLight: mix(pal.stoneLight, pal.woodLight, 0.4), trunk: mix(pal.stone, pal.trunk, 0.35), trunkDark: pal.stoneDark, woodDark: pal.stoneDark })
    return driftCache.get(pal)
}

// A Norway spruce: tall and narrow, drooping tiers, and on the snowfields a
// cap of snow along the top of every tier.
export function paintSpruce(ctx, pal, s, seed, snowy) {
    const r = rng(seed)
    const ink = pal.ink
    inkPoly(ctx, [[-s * 0.12, 0], [-s * 0.09, -s * 0.5], [s * 0.09, -s * 0.5], [s * 0.12, 0]], pal.trunkDark, ink, { line: 1.3 })
    const tiers = 5, height = s * 3.4
    for (let t = 0; t < tiers; t++) {
        const k = t / tiers
        const baseY = -s * 0.4 - k * height * 0.66
        const w = s * (0.95 - k * 0.7)
        const tip = baseY - height * 0.34
        const pts = [[0, tip]]
        const teeth = 6
        for (let i = 0; i <= teeth; i++) {
            const x = w - (i / teeth) * w * 2
            const droop = i % 2 ? -s * 0.05 : s * 0.07
            pts.push([x, baseY + droop + (r() - 0.5) * 3])
        }
        inkPoly(ctx, pts, pal.spruce, ink, { line: 1.5 })
        ctx.save()
        ctx.beginPath(); pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
        ctx.fillStyle = pal.spruceLight
        ctx.beginPath(); ctx.moveTo(0, tip); ctx.lineTo(-w * 1.1, baseY + 4); ctx.lineTo(-w * 0.1, baseY + 4); ctx.closePath(); ctx.fill()
        hatch(ctx, [0, tip, w, baseY - tip + 6], pal.spruceDark, { seed: seed + t, angle: -1.15, gap: 2.2, alpha: 0.65 })
        if (snowy) {
            // snow lies on the upper face of the tier
            ctx.fillStyle = pal.snowLight
            ctx.beginPath()
            ctx.moveTo(0, tip + 2)
            ctx.quadraticCurveTo(-w * 0.45, tip + (baseY - tip) * 0.45, -w * 0.95, baseY - s * 0.04)
            ctx.quadraticCurveTo(-w * 0.5, baseY - (baseY - tip) * 0.2, -w * 0.1, tip + (baseY - tip) * 0.55)
            ctx.quadraticCurveTo(w * 0.35, baseY - (baseY - tip) * 0.25, w * 0.9, baseY - s * 0.02)
            ctx.quadraticCurveTo(w * 0.4, tip + (baseY - tip) * 0.4, 0, tip + 2)
            ctx.fill()
            ctx.fillStyle = withAlpha(pal.snowShade, 0.9)
            ctx.beginPath(); ctx.ellipse(w * 0.5, baseY - (baseY - tip) * 0.18, w * 0.28, 2.2, 0.35, 0, Math.PI * 2); ctx.fill()
        }
        ctx.restore()
    }
    if (snowy) {
        ctx.fillStyle = pal.snowLight
        ctx.beginPath(); ctx.ellipse(0, -s * 0.4 - height * 0.66 * ((tiers - 1) / tiers) - height * 0.34 + 3, 3, 4, 0, 0, Math.PI * 2); ctx.fill()
    }
}

// A birch: one or two slender white stems marked with black, and a light,
// open crown of gold leaves.
export function paintBirch(ctx, pal, s, seed) {
    const r = rng(seed)
    const ink = pal.ink
    const stems = r() < 0.4 ? [[-s * 0.12, -0.06], [s * 0.1, 0.08]] : [[0, (r() - 0.5) * 0.08]]
    const top = -s * 2.1
    stems.forEach(([x0, lean]) => {
        const x1 = x0 + lean * s * 3
        inkPoly(ctx, [[x0 - s * 0.09, 0], [x1 - s * 0.05, top], [x1 + s * 0.05, top], [x0 + s * 0.09, 0]], pal.birchBark, ink, { line: 1.3 })
        for (let k = 0; k < 7; k++) {
            const t = 0.12 + r() * 0.8, y = t * top, x = x0 + (x1 - x0) * t
            line(ctx, [[x - s * 0.07 * (1 - t * 0.4), y], [x + (r() - 0.3) * s * 0.08, y - 1.5]], pal.birchMark, 1.2 + r(), 0.9)
        }
    })
    const cy = -s * 2.35
    const lobes = []
    const count = 7 + Math.floor(r() * 3)
    for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2 + r() * 0.6, d = s * (0.4 + r() * 0.3)
        lobes.push([Math.cos(a) * d * 0.9, cy + Math.sin(a) * d * 0.95 - s * 0.1, s * (0.3 + r() * 0.16)])
    }
    lobes.push([0, cy - s * 0.25, s * 0.42])
    inkedCircles(ctx, lobes, pal.birchLeafDark, ink, 1.5)
    ctx.save()
    clipCircles(ctx, lobes)
    lobes.forEach(([x, y, rad]) => { ctx.fillStyle = pal.birchLeaf; ctx.beginPath(); ctx.arc(x - rad * 0.18, y - rad * 0.2, rad * 0.8, 0, Math.PI * 2); ctx.fill() })
    lobes.forEach(([x, y, rad]) => { if (x < s * 0.2) { ctx.fillStyle = pal.birchLeafLight; ctx.beginPath(); ctx.arc(x - rad * 0.32, y - rad * 0.38, rad * 0.38, 0, Math.PI * 2); ctx.fill() } })
    // small leaves, and the branches showing through the gaps
    for (let i = 0; i < s * 3.4; i++) {
        const x = (r() - 0.5) * s * 1.9, y = cy + (r() - 0.55) * s * 1.7
        ctx.fillStyle = r() < 0.5 ? pal.birchLeafLight : pal.birchLeafDark
        ctx.globalAlpha = 0.7
        ctx.beginPath(); ctx.ellipse(x, y, 1.8, 1.1, r() * 3, 0, Math.PI * 2); ctx.fill()
    }
    ctx.globalAlpha = 1
    line(ctx, [[0, cy + s * 0.5], [-s * 0.4, cy - s * 0.1]], withAlpha(pal.birchMark, 0.7), 1)
    line(ctx, [[0, cy + s * 0.3], [s * 0.45, cy - s * 0.2]], withAlpha(pal.birchMark, 0.7), 1)
    ctx.restore()
}

// A boulder, with a cap of snow on the snowfields and lichen on the heath.
export function paintBoulder(ctx, pal, s, seed, snowy) {
    paintRock(ctx, pal, s, seed)
    const r = rng(seed + 3)
    if (snowy) {
        ctx.fillStyle = pal.snowLight
        ctx.beginPath()
        ctx.moveTo(-s * 0.85, -s * 0.35)
        ctx.quadraticCurveTo(-s * 0.4, -s * 1.05, s * 0.2, -s * 0.9)
        ctx.quadraticCurveTo(s * 0.75, -s * 0.8, s * 0.85, -s * 0.3)
        ctx.quadraticCurveTo(s * 0.2, -s * 0.5, -s * 0.85, -s * 0.35)
        ctx.fill()
        ctx.strokeStyle = withAlpha(pal.snowDeep, 0.8); ctx.lineWidth = 1; ctx.stroke()
    } else {
        for (let i = 0; i < 5; i++) {
            ctx.fillStyle = withAlpha(pal.night ? "#6A7A5A" : "#C9C77A", 0.8)
            ctx.beginPath(); ctx.arc((r() - 0.5) * s, -s * (0.3 + r() * 0.4), 1.5 + r() * 2, 0, Math.PI * 2); ctx.fill()
        }
    }
}

function heatherClump(ctx, pal, x, y, seed) {
    const r = rng(seed)
    for (let i = 0; i < 9; i++) {
        const fx = x + (r() - 0.5) * 20, fy = y + (r() - 0.5) * 9
        line(ctx, [[fx, fy + 5], [fx + (r() - 0.5) * 2, fy - 2]], pal.heathDark, 1)
        ctx.fillStyle = r() < 0.5 ? pal.heather : pal.heatherDeep
        for (let k = 0; k < 3; k++) { ctx.beginPath(); ctx.arc(fx + (r() - 0.5) * 2, fy - 2 - k * 2, 1.3, 0, Math.PI * 2); ctx.fill() }
    }
}

function lingon(ctx, pal, x, y, seed) {
    const r = rng(seed)
    for (let i = 0; i < 6; i++) {
        const fx = x + (r() - 0.5) * 16, fy = y + (r() - 0.5) * 8
        ctx.fillStyle = pal.spruceLight; ctx.beginPath(); ctx.ellipse(fx, fy, 2.6, 1.4, r() * 3, 0, Math.PI * 2); ctx.fill()
        if (r() < 0.6) { ctx.fillStyle = pal.lingon; ctx.beginPath(); ctx.arc(fx + 1.5, fy - 1, 1.5, 0, Math.PI * 2); ctx.fill() }
    }
}

function cottongrass(ctx, pal, x, y, seed) {
    const r = rng(seed)
    for (let i = 0; i < 6; i++) {
        const fx = x + (r() - 0.5) * 16, fy = y + (r() - 0.5) * 8, h = 7 + r() * 5
        line(ctx, [[fx, fy], [fx + (r() - 0.5) * 3, fy - h]], pal.heathDark, 1)
        ctx.fillStyle = pal.snowLight; ctx.beginPath(); ctx.arc(fx, fy - h - 1.5, 2.2, 0, Math.PI * 2); ctx.fill()
        ctx.strokeStyle = withAlpha(pal.snowDeep, 0.7); ctx.lineWidth = 0.6; ctx.stroke()
    }
}
